import { FC, useContext } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { AccountContext, EDITOR_ROLES } from "auth/accountProvider";
import { format, parseISO } from "date-fns";
import { CpiDashboardData } from "pages/cpiReports/types";
import {
  LastUpload,
  isTileLoading,
  useLastUpload,
  useDataQualityCount,
} from "../overviewData";
import KpiTile from "./KpiTile";
import { UploadIcon, OrdersIcon, ShipmentsIcon, AlertIcon } from "./icons";

interface DashboardQuery {
  data: CpiDashboardData | null | undefined;
  isError: boolean;
  refetch: () => void;
}

interface StatusKpisProps {
  dashboard: DashboardQuery;
  lastUpload: ReturnType<typeof useLastUpload>;
  dqCount: ReturnType<typeof useDataQualityCount>;
}

const formatCount = (n: number | null | undefined): string =>
  typeof n === "number" ? n.toLocaleString() : "—";

const StatusKpis: FC<StatusKpisProps> = ({ dashboard, lastUpload, dqCount }) => {
  const { t } = useTranslation();
  const { hasRole } = useContext(AccountContext);

  // /data is editor-gated. Overview is a viewer surface, so linking there
  // unconditionally would bounce a viewer off to /no-access. Viewers keep the
  // KPI itself; only the navigation affordance is withheld.
  const canOpenIngestionIssues = hasRole(EDITOR_ROLES);

  const upload: LastUpload | null | undefined = lastUpload.data;

  // Approximate quality score: issue records as a share of ingested records
  // (business orders + shipments). Not a precise per-record audit — issues can
  // span multiple records or a single record can have multiple issues — but it
  // gives a directional, easy-to-read percentage in place of a raw issue count.
  const totalRecords =
    (dashboard.data?.businessOrderCount ?? 0) + (dashboard.data?.shipmentCount ?? 0);
  const issues = dqCount.data ?? 0;
  const qualityScore =
    totalRecords > 0 ? Math.max(0, 100 * (1 - issues / totalRecords)) : null;
  const qualityLoading = isTileLoading(dashboard) || isTileLoading(dqCount);

  return (
    <div className="overview-kpis">
      <KpiTile
        label={t("dashboard.lastUpload")}
        icon={<UploadIcon />}
        loading={isTileLoading(lastUpload)}
        error={lastUpload.isError}
        onRetry={lastUpload.refetch}
        compactValue
        value={
          upload ? format(parseISO(upload.uploadedAt), "d MMM yyyy") : t("dashboard.noUploads")
        }
        sub={
          upload && (
            <>
              <span
                className={`kpi-chip kpi-chip--${
                  upload.overallSuccess ? "ok" : "bad"
                }`}
              >
                {upload.overallSuccess ? t("dashboard.success") : t("dashboard.failed")}
              </span>{" "}
              {upload.filename}
            </>
          )
        }
      />

      <KpiTile
        label={t("dashboard.ordersThisPeriod")}
        icon={<OrdersIcon />}
        loading={isTileLoading(dashboard)}
        error={dashboard.isError}
        onRetry={dashboard.refetch}
        value={formatCount(dashboard.data?.businessOrderCount)}
        sub={t("dashboard.acrossDatasets")}
      />

      <KpiTile
        label={t("dashboard.shipmentsThisPeriod")}
        icon={<ShipmentsIcon />}
        loading={isTileLoading(dashboard)}
        error={dashboard.isError}
        onRetry={dashboard.refetch}
        value={formatCount(dashboard.data?.shipmentCount)}
        sub={t("dashboard.acrossDatasets")}
      />

      <KpiTile
        label={t("dashboard.dataQuality")}
        icon={<AlertIcon />}
        tone={qualityScore !== null && qualityScore < 95 ? "danger" : "default"}
        loading={qualityLoading}
        error={dashboard.isError || dqCount.isError}
        onRetry={() => {
          dashboard.refetch();
          dqCount.refetch();
        }}
        value={qualityScore !== null ? `${qualityScore.toFixed(1)}%` : "—"}
        sub={
          canOpenIngestionIssues ? (
            <Link to="/data?tab=ingestion-issues" className="kpi-tile__link">
              {t("dashboard.reviewIngestionIssues")}
            </Link>
          ) : (
            t("dashboard.dataQualityIssues")
          )
        }
      />
    </div>
  );
};

export default StatusKpis;
